/**
 * The one number the whole family can read.
 *
 * Three rules this component exists to keep:
 *
 * 1. The figure is always readable as text. Position and colour are the
 *    decoration; a child who cannot see the bar, or a parent glancing at a
 *    greyscale screenshot, still gets the answer. The bar never carries
 *    meaning the words don't.
 * 2. Below zero is never clamped. Overdraw is the most honest lesson in the
 *    product — you can spend money you don't have, and it comes out of the
 *    future — so it gets its own colour, its own label, and its own icon.
 * 3. Empty is not failure. See FuelGauge.css for why nothing turns amber on
 *    the way down.
 */

import { formatCents } from '../domain/money.js';
import { CADENCES, periodIndexAt, periodStart, dayNumber } from '../domain/periods.js';
import { SpotArt } from './Art.jsx';
import './FuelGauge.css';

const CADENCE_NOUN = { weekly: 'this week', biweekly: 'this fortnight', monthly: 'this month' };

function WarningIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true">
      <path d="M8 1.8 15 14H1L8 1.8Z" strokeLinejoin="round" />
      <path d="M8 6.4v3.2M8 11.9v.1" strokeLinecap="round" />
    </svg>
  );
}

function GoalIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true">
      <circle cx="8" cy="8" r="6.2" />
      <circle cx="8" cy="8" r="2.4" />
    </svg>
  );
}

/** Whole days until the next refill, from calendar dates only. */
function daysUntilRefill(today, cadence) {
  const next = periodStart(periodIndexAt(today, cadence) + 1, cadence);
  return dayNumber(next) - dayNumber(today);
}

function refillPhrase(days) {
  if (days <= 0) return 'refills today';
  if (days === 1) return 'refills tomorrow';
  return `refills in ${days} days`;
}

/**
 * Before a refill rule exists there is no gauge, only an explanation.
 *
 * The scope sentence is not filler. A visible family number invites a child to
 * audit the household — "you got coffee at work" — and the fix is to say
 * plainly, in the one place everyone reads, that this pot is the fun money and
 * nothing else lives in it. CODING_PLAN §14, "watch for a child policing the
 * family."
 */
function FuelGaugeEmpty({ onSetUp }) {
  return (
    <section className="card gauge" aria-labelledby="fuel-heading">
      <p className="eyebrow" id="fuel-heading">Family fuel</p>
      {/* Decorative, and only on the empty state. Once there is a real balance
          the number is the thing to look at and a picture beside it is
          competition. */}
      <SpotArt name="fuel" />
      <h2>One pot for the fun stuff</h2>
      <p className="small muted">
        Ice cream, pizza, a trip to the shops. It refills on a day you pick, everyone can
        see what’s in it, and what nobody spends goes toward something bigger.
      </p>
      <p className="small muted">
        Only the fun money lives here — never bills, groceries, or anything else the
        grown-ups are handling.
      </p>
      {onSetUp && <button className="primary" onClick={onSetUp}>Set up the tank</button>}
    </section>
  );
}

export default function FuelGauge({
  balanceCents,
  refillCents,
  cadence = 'weekly',
  today,
  goal = null,
  rolledOverCents = 0,
  onSetUp,
}) {
  if (!refillCents || refillCents <= 0) return <FuelGaugeEmpty onSetUp={onSetUp} />;

  const isOverdrawn = balanceCents < 0;
  const period = CADENCE_NOUN[cadence] ?? CADENCE_NOUN.weekly;
  const days = daysUntilRefill(today, cadence);

  // Overdrawn bars measure the debt against the refill that will absorb it,
  // which is the number that actually answers "how bad is this?".
  const denominator = refillCents > 0 ? refillCents : 1;
  const ratio = isOverdrawn
    ? Math.min(1, -balanceCents / denominator)
    : Math.min(1, balanceCents / denominator);

  // A tank can hold more than one refill — whenever nothing is rolling the
  // leftovers out, the balance outgrows the period's amount. "left of $60.00
  // this week" beside $180.00 is simply false, so the caption changes rather
  // than the number being made to fit the sentence.
  const carriedCents = Math.max(0, balanceCents - refillCents);

  const valueText = isOverdrawn
    ? `${formatCents(-balanceCents)} borrowed from the next refill`
    : carriedCents > 0
      ? `${formatCents(balanceCents)} in the tank, including ${formatCents(carriedCents)} carried over`
      : `${formatCents(balanceCents)} left of ${formatCents(refillCents)} ${period}`;

  return (
    <section className="card gauge" aria-labelledby="fuel-heading">
      <div className="spread">
        <p className="eyebrow" id="fuel-heading">Family fuel</p>
        <p className="small muted">{refillPhrase(days)}</p>
      </div>

      {/*
        Logging a spend changes this number while the screen is already open, so
        it announces itself. Without the live region a screen-reader user taps
        "Ice cream, $6" and hears nothing at all — the one confirmation that
        the log actually landed is the number moving.
      */}
      <div className="gauge__figure" aria-live="polite">
        {/*
          The sign stays on. Dropping it and letting the BORROWED pill carry the
          meaning puts "$8.00" in the largest type on the screen for a family
          that is eight dollars down — which reads as money they have. The pill
          is the redundancy, never the only signal.
        */}
        <span className={`gauge__amount${isOverdrawn ? ' gauge__amount--overdrawn' : ''}`}>
          {formatCents(balanceCents)}
        </span>
        {isOverdrawn && (
          <span className="gauge__flag"><WarningIcon />Borrowed</span>
        )}
      </div>

      {/*
        role="meter" is the honest semantic for a level rather than a task's
        progress. aria-valuetext carries the sentence because a bare number
        read aloud out of context ("negative eight hundred") is worse than no
        number at all.
      */}
      <div
        className="gauge__track"
        role="meter"
        aria-valuemin={0}
        aria-valuemax={refillCents}
        aria-valuenow={Math.max(0, balanceCents)}
        aria-valuetext={valueText}
      >
        <div
          className={`gauge__fill${isOverdrawn ? ' gauge__fill--overdrawn' : ''}`}
          style={{ width: `${ratio * 100}%` }}
        />
      </div>

      <div className="gauge__caption">
        <p className="small muted">
          {isOverdrawn
            ? <>Coming out of next {cadence === 'monthly' ? 'month' : 'week'}’s fuel.</>
            : carriedCents > 0
              ? <>including {formatCents(carriedCents)} carried over</>
              : <>left of {formatCents(refillCents)} {period}</>}
        </p>
        {rolledOverCents > 0 && goal && (
          <p className="small muted">{formatCents(rolledOverCents)} rolled over</p>
        )}
      </div>

      {/*
        No rollover promise while the tank is negative. "What's left goes to the
        beach trip" next to a below-zero balance is the app telling a family
        they're contributing to something when they're actually behind — the
        exact species of comfortable inaccuracy that makes the gauge stop being
        believed.
      */}
      {goal && !isOverdrawn && (
        <p className="gauge__rollover">
          <GoalIcon />
          <span>
            What’s left goes to <strong>{goal.name}</strong> — {formatCents(goal.savedCents)} of{' '}
            {formatCents(goal.targetCents)}
          </span>
        </p>
      )}
      {goal && isOverdrawn && (
        <p className="gauge__rollover">
          <GoalIcon />
          <span>
            Nothing heads to <strong>{goal.name}</strong> until the tank is back above zero.
          </span>
        </p>
      )}
    </section>
  );
}
